import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { gsap } from "./animations/gsap.config";
import { ScrollTrigger } from "./animations/gsap.config";
import { ArrowUp } from "lucide-react"; // Smooth chevron arrow icon

// Layout
import Layout from "./components/layout/Layout";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Blog from "./pages/Blog";
import Portfolio from "./pages/Portfolio";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";

// Blog Detail Component
import BlogDetail from "./components/blog/BlogDetail";

// Portfolio Detail Component
import PortfolioDetail from "./components/portfolio/PortfolioDetail";

// Context
import { AppProvider } from "./context/AppContext";
import { ToastBar, Toaster } from "react-hot-toast";

function App() {
  const lenisRef = useRef(null);
  const buttonRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let lenis;

    // Dynamically import Lenis to avoid SSR issues
    import("lenis").then(({ default: Lenis }) => {
      lenis = new Lenis({
        duration: 1.4,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smooth: true,
        mouseMultiplier: 1,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false,
      });

      lenisRef.current = lenis;

      // Connect Lenis to GSAP ScrollTrigger
      lenis.on("scroll", (e) => {
        ScrollTrigger.update();

        // Show button if user has scrolled down more than 400px
        if (e.scroll > 400) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      });

      const tickerCallback = (time) => {
        lenis.raf(time * 1000);
      };

      gsap.ticker.add(tickerCallback);
      gsap.ticker.lagSmoothing(0);
    });

    return () => {
      if (lenisRef.current) {
        gsap.ticker.remove((time) => lenisRef.current?.raf(time * 1000));
        lenisRef.current.destroy();
      }
    };
  }, []);

  // GSAP Fade In/Out animation logic for the button
  useEffect(() => {
    if (isVisible) {
      gsap.to(buttonRef.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: "power2.out",
        pointerEvents: "auto",
      });
    } else {
      gsap.to(buttonRef.current, {
        opacity: 0,
        y: 20,
        scale: 0.8,
        duration: 0.3,
        ease: "power2.in",
        pointerEvents: "none",
      });
    }
  }, [isVisible]);

  // Smooth scroll handler utilizing the Lenis instance instance
  const handleScrollToTop = () => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, {
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  return (
    <AppProvider>
      <Toaster position="top-center" reverseOrder={true} />
      <Router>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/projects" element={<Portfolio />} />
            <Route path="/projects/:slug" element={<PortfolioDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
          </Routes>
        </Layout>
      </Router>

      {/* Floating Scroll To Top Button */}
      <button
        ref={buttonRef}
        onClick={handleScrollToTop}
        aria-label="Scroll to top"
        style={{
          opacity: 0,
          transform: "translateY(20px) scale(0.8)",
          pointerEvents: "none",
        }}
        className="fixed bottom-6 right-6 z-50 p-3.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 border border-slate-800 dark:border-slate-200 shadow-xl cursor-pointer transition-transform duration-200 hover:scale-110 active:scale-95 group"
      >
        <ArrowUp
          size={20}
          className="transition-transform duration-300 group-hover:-translate-y-0.5"
        />
      </button>
    </AppProvider>
  );
}

export default App;
